import React from "react";

import { useFormikContext } from "formik";
import moment from "moment";
import { CurrentFinanceYear } from "../../../../config/app-constants";
import { ISaleLandAndBuildingForm } from "../../../../config/interfaces";
import { UtilService } from "../../../../services";
import { DateTimePicker, FormField, NumericInputField, SelectInputField } from "../../../fields";

type Props = {};

const CGSaleLandAndBuildingInfoForm: React.FC<Props> = (props: Props) => {
  const {} = props;

  const { values, errors, touched, setFieldValue, handleBlur } =
    useFormikContext<ISaleLandAndBuildingForm>();

  const getNetIncomes = (
    salePrice: string,
    stampDuty: string,
    purchasePrice: string,
    costImprovement: string,
    transferExpenses: string,
    deduction: string
  ) => {
    const maxNumber = Math.max(Number(salePrice || 0), Number(stampDuty || 0));
    const total =
      Number(purchasePrice || 0) + Number(costImprovement || 0) + Number(transferExpenses || 0);
    const balance = maxNumber - total;
    const capitalGains = balance - Number(deduction || 0);
    setFieldValue("SaleConsideration", maxNumber);
    setFieldValue("Balance", balance);
    setFieldValue("CapitalGains", capitalGains);
  };

  return (
    <>
      <section className="mb-5">
        <FormField label="Date of Sale" isRequired={true} sm={4}>
          <DateTimePicker
            name="SaleDate"
            placeholder="DD/MM/YYYY"
            value={values.SaleDate ? moment(values.SaleDate).toDate() : ""}
            onChange={(date) => {
              if (date) {
                const _date = UtilService.convertToStandardDateFormat(date);
                setFieldValue("SaleDate", _date);
              } else {
                setFieldValue("SaleDate", "");
              }
            }}
            onBlur={handleBlur("SaleDate")}
            minDate={
              values?.PurchaseDate
                ? moment(values.PurchaseDate).toDate()
                : moment(CurrentFinanceYear).toDate()
            }
            maxDate={moment(CurrentFinanceYear).toDate()}
            isInvalid={Boolean(errors.SaleDate) && touched.SaleDate}
            errorMsg={errors.SaleDate}
            isClearable={true}
          />
        </FormField>

        <FormField label="Date of Purchase" isRequired={true} sm={4}>
          <DateTimePicker
            name="PurchaseDate"
            placeholder="DD/MM/YYYY"
            value={values.PurchaseDate ? moment(values.PurchaseDate).toDate() : ""}
            onChange={(date) => {
              if (date) {
                const _date = UtilService.convertToStandardDateFormat(date);
                setFieldValue("PurchaseDate", _date);
              } else {
                setFieldValue("PurchaseDate", "");
              }
            }}
            onBlur={handleBlur("PurchaseDate")}
            minDate={undefined}
            maxDate={
              values?.SaleDate
                ? moment(values.SaleDate).toDate()
                : moment(CurrentFinanceYear).toDate()
            }
            isInvalid={Boolean(errors.PurchaseDate) && touched.PurchaseDate}
            errorMsg={errors.PurchaseDate}
            isClearable={true}
          />
        </FormField>

        <FormField label="Description of asset sold" isRequired={true} sm={4}>
          <SelectInputField
            name="DescriptionAssetSold"
            isOptionArrayOfObject={false}
            options={["Land", "Building", "Both"]}
            value={[values.DescriptionAssetSold]}
            onChange={(_unutilizedCapitalGains) => {
              if (_unutilizedCapitalGains) {
                setFieldValue("DescriptionAssetSold", _unutilizedCapitalGains.value);
              } else {
                setFieldValue("DescriptionAssetSold", undefined);
              }
            }}
            onBlur={handleBlur("DescriptionAssetSold")}
            isInvalid={Boolean(errors.DescriptionAssetSold) && touched.DescriptionAssetSold}
            errorMsg={errors.DescriptionAssetSold}
          />
        </FormField>

        <FormField label="Total Sale Price" isRequired={true} sm={4}>
          <NumericInputField
            showLeftIcon={true}
            placeholder=""
            name="TotalSalePrice"
            value={values.TotalSalePrice}
            onChange={(value) => {
              setFieldValue("TotalSalePrice", value);
              getNetIncomes(
                value,
                values.StampDuty,
                values.TotalPurchasePrice,
                values.CostImprovementDetails,
                values.TransferExpense,
                values.Deduction
              );
            }}
            onBlur={handleBlur("TotalSalePrice")}
            isInvalid={Boolean(errors.TotalSalePrice) && touched.TotalSalePrice}
            errorMsg={errors.TotalSalePrice}
          />
        </FormField>
        <FormField label="Total Stamp Duty Value" isRequired={true} sm={4}>
          <NumericInputField
            showLeftIcon={true}
            placeholder=""
            name="StampDuty"
            value={values.StampDuty}
            onChange={(value) => {
              setFieldValue("StampDuty", value);
              getNetIncomes(
                values.TotalSalePrice,
                value,
                values.TotalPurchasePrice,
                values.CostImprovementDetails,
                values.TransferExpense,
                values.Deduction
              );
            }}
            onBlur={handleBlur("StampDuty")}
            isInvalid={Boolean(errors.StampDuty) && touched.StampDuty}
            errorMsg={errors.StampDuty}
          />
        </FormField>
        <FormField label="Total Sale consideration" isRequired={true} sm={4}>
          <NumericInputField
            showLeftIcon={true}
            placeholder=""
            name="SaleConsideration"
            value={values.SaleConsideration}
            onChange={(value) => {
              setFieldValue("SaleConsideration", value);
            }}
            onBlur={handleBlur("SaleConsideration")}
            isInvalid={Boolean(errors.SaleConsideration) && touched.SaleConsideration}
            errorMsg={errors.SaleConsideration}
            isDisabled={true}
          />
        </FormField>

        <FormField label="Total Purchase Price" isRequired={true} sm={4}>
          <NumericInputField
            showLeftIcon={true}
            placeholder=""
            name="TotalPurchasePrice"
            value={values.TotalPurchasePrice}
            onChange={(value) => {
              setFieldValue("TotalPurchasePrice", value);
              getNetIncomes(
                values.TotalSalePrice,
                values.StampDuty,
                value,
                values.CostImprovementDetails,
                values.TransferExpense,
                values.Deduction
              );
            }}
            onBlur={handleBlur("TotalPurchasePrice")}
            isInvalid={Boolean(errors.TotalPurchasePrice) && touched.TotalPurchasePrice}
            errorMsg={errors.TotalPurchasePrice}
          />
        </FormField>
        <FormField label="Total cost of improvement" sm={4}>
          <NumericInputField
            showLeftIcon={true}
            placeholder=""
            name="CostImprovementDetails"
            value={values.CostImprovementDetails}
            onChange={(value) => {
              setFieldValue("CostImprovementDetails", value);
              getNetIncomes(
                values.TotalSalePrice,
                values.StampDuty,
                values.TotalPurchasePrice,
                value,
                values.TransferExpense,
                values.Deduction
              );
            }}
            onBlur={handleBlur("CostImprovementDetails")}
            isInvalid={Boolean(errors.CostImprovementDetails) && touched.CostImprovementDetails}
            errorMsg={errors.CostImprovementDetails}
          />
        </FormField>
        <FormField label="Expenses relating to transfer" sm={4}>
          <NumericInputField
            showLeftIcon={true}
            name="TransferExpense"
            value={values.TransferExpense}
            onChange={(value) => {
              setFieldValue("TransferExpense", value);
              getNetIncomes(
                values.TotalSalePrice,
                values.StampDuty,
                values.TotalPurchasePrice,
                values.CostImprovementDetails,
                value,
                values.Deduction
              );
            }}
            onBlur={handleBlur("TransferExpense")}
            isInvalid={Boolean(errors.TransferExpense) && touched.TransferExpense}
            errorMsg={errors.TransferExpense}
          />
        </FormField>
        <FormField label="Balance" sm={4}>
          <NumericInputField
            showLeftIcon={true}
            name="Balance"
            value={values.Balance}
            onChange={(value) => setFieldValue("Balance", Number(value))}
            onBlur={handleBlur("Balance")}
            isInvalid={Boolean(errors.Balance) && touched.Balance}
            errorMsg={errors.Balance}
            isDisabled={true}
            allowNegative={true}
          />
        </FormField>
        <FormField label="Deductions u/s 54" sm={4}>
          <NumericInputField
            showLeftIcon={true}
            name="Deduction"
            value={values.Deduction}
            onChange={(value) => {
              setFieldValue("Deduction", Number(value));
              getNetIncomes(
                values.TotalSalePrice,
                values.StampDuty,
                values.TotalPurchasePrice,
                values.CostImprovementDetails,
                values.TransferExpense,
                value
              );
            }}
            onBlur={handleBlur("Deduction")}
            isInvalid={Boolean(errors.Deduction) && touched.Deduction}
            errorMsg={errors.Deduction}
          />
        </FormField>

        <FormField label="Capital gains chargable to tax" sm={4}>
          <NumericInputField
            showLeftIcon={true}
            placeholder=""
            name="CapitalGains"
            value={values.CapitalGains}
            onChange={(value) => setFieldValue("CapitalGains", value)}
            onBlur={handleBlur("CapitalGains")}
            isInvalid={Boolean(errors.CapitalGains) && touched.CapitalGains}
            errorMsg={errors.CapitalGains}
            isDisabled={true}
            allowNegative={true}
          />
        </FormField>
      </section>
    </>
  );
};

export default CGSaleLandAndBuildingInfoForm;
